import { observable } from "@legendapp/state";
import { T1_ROLE } from "@/constants/roles";
import { fetchT1UserSession } from "@/apis/clients/t1UserClient";
import type { T1UserSession } from "@/apis/clients/t1UserClient";

interface UserState {
  t1Session: T1UserSession | null;
  /** False once the first T1 session fetch has settled, either way. */
  isResolving: boolean;
}

const defaultValues: UserState = {
  t1Session: null,
  isResolving: true,
};

export const UserStore$ = observable(defaultValues);

export const setUserInfo = (t1Session: T1UserSession | null): void => {
  UserStore$.assign({ t1Session, isResolving: false });
};

export const fetchUserInfo = async (): Promise<void> => {
  const t1Session = await fetchT1UserSession();
  setUserInfo(t1Session);
};

/**
 * True for Infillion employees and global admins. These users can see all
 * agencies and internal hierarchy data. Use `isGlobalAdmin` for surfaces that
 * the backend gates behind requireGlobalAdmin (e.g. User Management).
 */
export const isInfillionUser = (): boolean => {
  const t1Session = UserStore$.t1Session.get();
  if (!t1Session) return false;
  return (
    t1Session.is_global_admin || t1Session.roles.includes(T1_ROLE.INTERNAL)
  );
};

/** True only for global admins — matches the backend's requireGlobalAdmin guard. */
export const isGlobalAdmin = (): boolean => {
  const t1Session = UserStore$.t1Session.get();
  if (!t1Session) return false;
  return t1Session.is_global_admin;
};

/**
 * True for users whose only T1 role is agency-scoped. These users see only
 * their own agency's data and no internal admin surfaces.
 */
export const isAgencyUser = (): boolean => {
  const t1Session = UserStore$.t1Session.get();
  if (!t1Session) return false;
  return t1Session.roles.includes(T1_ROLE.AGENCY) && !isInfillionUser();
};
